import React from 'react';
import { RiErrorWarningLine, RiInformationLine } from 'react-icons/ri';
import ValidatedInput from '../ui/ValidatedInput';
import ValidatedTextarea from '../ui/ValidatedTextarea';
import Select from '../ui/Select';
import SearchableSelect from '../ui/SearchableSelect';
import { Sim, SimStatus, SimType } from '../../services/simService';
import type { SimPackageOption } from '../../services/simPackageService';
import { parsePhoneNumbers } from '../../utils/simPhone';
import {
  SimFormData,
  SimFormMode,
  SIM_OPERATOR_OPTIONS,
  getSimOperatorOptions,
  isSimOperatorRequired,
  packageMatchesSimType,
  packagePriceLabel,
} from './simForm';

/**
 * ช่องกรอกของฟอร์มซิม (เพิ่ม/แก้ไข) — ยกมาจาก renderForm ของโมดัลใน SimPage ครบทุกช่อง (หน้าเต็ม 28 ต.ค. 2026)
 * setFormData แบบ functional ทุกจุด
 */
export interface SimFormFieldsProps {
  mode: SimFormMode;
  formData: SimFormData;
  setFormData: React.Dispatch<React.SetStateAction<SimFormData>>;
  formErrors: Record<string, string>;
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  codeError: string;
  simPackages: SimPackageOption[];
  /** ผู้ลงทะเบียน (GET /users/performers) */
  users: Array<{ id: number; full_name?: string | null; username: string }>;
  /** ผู้ใช้ที่ล็อกอิน — ถ้าไม่อยู่ในรายการ performers ให้เติมไว้บนสุด (เหมือนโมดัลเดิม) */
  currentUser?: { id: number; name?: string; email?: string } | null;
  /** ซิมตอนเปิดฟอร์ม (โหมดแก้ไข) */
  original?: Sim | null;
}

const SimFormFields: React.FC<SimFormFieldsProps> = ({
  mode, formData, setFormData, formErrors, setFormErrors, codeError, simPackages, users, currentUser, original,
}) => {
  const selectedSim = original ?? null;
  const user = currentUser ?? null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const processedValue = type === 'number' ? parseFloat(value) : value;
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Handler เมื่อเลือกแพ็กเกจจาก dropdown — pre-fill ฟิลด์ที่ override ได้
  // หมายเหตุ: ไม่ set package_name ในฟอร์มแล้ว (BE เป็นผู้ snapshot จาก sim_packages.name)
  const handlePackageSelect = (packageId: number | null) => {
    if (!packageId) {
      setFormData((prev) => ({
        ...prev,
        sim_package_id: null,
        speed_mbps: null,
        billing_cycle: 'MONTHLY',
        monthly_fee: 0,
        yearly_cost: null,
      }));
      return;
    }
    const selectedPackage = simPackages.find((pkg) => pkg.id === packageId);
    if (selectedPackage) {
      // C9 + B-C9-2: ค่า operator ของฟอร์มใช้ตัวพิมพ์ใหญ่ตรงกับ enum Carrier แล้ว
      // จึงไม่ต้องมีตารางแปลงค่าอีก — รับ carrier มาใช้ตรง ๆ ได้ทุกค่าย (รวม NT/OTHER)
      // แต่ยังกันค่าประหลาดจาก backend: ถ้าไม่อยู่ในตัวเลือกที่ฟอร์มมี ให้คงค่าเดิมไว้
      const knownOperators = SIM_OPERATOR_OPTIONS.map((o) => o.value);
      setFormData((prev) => ({
        ...prev,
        sim_package_id: packageId,
        speed_mbps: selectedPackage.speed_mbps || null,
        billing_cycle: selectedPackage.billing_cycle as 'MONTHLY' | 'YEARLY',
        monthly_fee: selectedPackage.monthly_cost || 0,
        yearly_cost: selectedPackage.yearly_cost || null,
        operator: knownOperators.includes(selectedPackage.carrier) ? selectedPackage.carrier : prev.operator,
      }));
    }
  };

  // เปลี่ยนประเภทซิม: ล้างแพ็กเกจที่รอบบิลไม่ตรงประเภทใหม่ (กัน snapshot ค้าง submit แล้วโดน 400)
  // และล้างวันหมดอายุเมื่อเป็นรายเดือน (ซิมรายเดือนไม่มีวันหมดอายุ — PO decision)
  const handleSimTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const newType = e.target.value as SimType;
    setFormData((prev) => {
      const next = { ...prev, sim_type: newType };
      if (newType === SimType.POSTPAID) next.expiry_date = '';
      if (prev.sim_package_id) {
        const pkg = simPackages.find((p) => p.id === prev.sim_package_id);
        if (!pkg || !packageMatchesSimType(pkg, newType)) {
          next.sim_package_id = null;
          next.speed_mbps = null;
          next.billing_cycle = 'MONTHLY';
          next.monthly_fee = 0;
          next.yearly_cost = null;
        }
      }
      return next;
    });
    if (formErrors.sim_type) setFormErrors((prev) => ({ ...prev, sim_type: '' }));
  };

  // ผู้ลงทะเบียน — OBS-S1362-A: users มาจาก getPerformers() (id/full_name/username)
  const userOptions = users.map((u) => ({ value: u.id, label: `${u.full_name || u.username} (${u.username})` }));
  if (user && !users.find((u) => u.id === user.id)) {
    userOptions.unshift({ value: user.id, label: `${user.name} (${user.email}) - ผู้ใช้ปัจจุบัน` });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
        <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
          <RiInformationLine className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-base font-semibold text-secondary-800">ข้อมูลพื้นฐาน</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <ValidatedInput
            label="รหัสซิม"
            name="code"
            value={formData.code}
            onChange={() => { /* รหัสออกโดยระบบ แก้ไม่ได้ (เหมือนโมดัลเดิม) */ }}
            error={formErrors.code}
            required
            disabled={true}
            placeholder="รหัสจะสร้างอัตโนมัติ"
          />
          {codeError && (
            <p className="mt-1 text-sm text-danger-600 flex items-center">
              <RiErrorWarningLine className="w-4 h-4 mr-1" />
              {codeError}
            </p>
          )}
        </div>
        {mode === 'create' ? (
          <div>
            <label className="block text-sm font-medium text-secondary-700">
              เบอร์โทรศัพท์ <span className="text-danger-500">*</span>
            </label>
            <textarea
              aria-label="เบอร์โทรศัพท์"
              name="phone_number"
              rows={3}
              value={formData.phone_number}
              onChange={handleInputChange}
              className={`mt-1 input ${formErrors.phone_number ? 'border-danger-500' : ''}`}
              placeholder={'กรอกได้หลายเบอร์ — คั่นด้วย , หรือขึ้นบรรทัดใหม่ (เบอร์ละ 10 หลัก)\nเช่น\n0811111111\n0822222222, 0833333333'}
              aria-invalid={Boolean(formErrors.phone_number)}
              aria-describedby={formErrors.phone_number ? 'phone_number-error' : undefined}
            />
            {(() => {
              const n = parsePhoneNumbers(formData.phone_number).length;
              return n > 1 ? (
                <p className="mt-1 text-xs text-info-600" data-testid="sim-bulk-phone-hint">
                  จะสร้าง {n} ซิม — ระบบสร้างรหัสซิมให้อัตโนมัติแต่ละใบ
                </p>
              ) : (
                <p className="mt-1 text-xs text-secondary-500">กรอกหลายเบอร์พร้อมกันได้ (คั่นด้วย , หรือขึ้นบรรทัดใหม่)</p>
              );
            })()}
            {formErrors.phone_number && (
              <p id="phone_number-error" className="mt-1 text-sm text-danger-600">{formErrors.phone_number}</p>
            )}
          </div>
        ) : (
          <ValidatedInput
            label="เบอร์โทรศัพท์"
            name="phone_number"
            type="tel"
            value={formData.phone_number}
            onChange={handleInputChange}
            error={formErrors.phone_number}
            required
            placeholder="0XXXXXXXXX"
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="ค่ายมือถือ"
          name="operator"
          value={formData.operator}
          onChange={handleInputChange}
          error={formErrors.operator}
          /* กติกาเดียวกับ validateSimForm — แถวเก่าที่ค่ายว่างอยู่แล้วไม่ต้องบังคับ (ดอกจันต้องไม่หลอก) */
          required={isSimOperatorRequired(mode, selectedSim?.operator)}
        >
          {/* B-C9-2: ชุดเดียวกับตัวกรอง + คงค่าเดิมของแถวไว้เสมอถ้าไม่อยู่ในชุดมาตรฐาน (prod 'True' 75 / NULL 2) */}
          {getSimOperatorOptions(formData.operator).map((op) => (
            <option key={op.value || '__empty__'} value={op.value}>{op.label}</option>
          ))}
        </Select>

        <Select
          label="ประเภทซิม"
          name="sim_type"
          value={formData.sim_type}
          onChange={handleSimTypeChange}
          error={formErrors.sim_type}
          required
        >
          <option value={SimType.PREPAID}>เติมเงิน</option>
          <option value={SimType.POSTPAID}>รายเดือน</option>
          <option value={SimType.YEARLY}>รายปี</option>
          <option value={SimType.CORPORATE}>องค์กร</option>
          <option value={SimType.OTHER}>อื่นๆ</option>
        </Select>
      </div>

      {/* แพ็กเกจซิม — เลือกจาก master (sim_packages) ช่องเดียว แทน 3 ช่องเดิม */}
      <div className="mt-4 p-4 border border-primary-200 rounded-lg bg-primary-50">
        <label className="block text-sm font-medium text-primary-700 mb-2">
          แพ็กเกจซิม {mode === 'create' && <span className="text-danger-500">*</span>}
        </label>
        <SearchableSelect
          name="sim_package_id"
          options={simPackages
            // กรองตามประเภทซิม — คงแพ็กเกจที่เลือกอยู่ไว้ให้แถว legacy ที่รอบบิลไม่ตรงยังแสดงชื่อได้
            .filter(pkg => pkg.id === formData.sim_package_id || packageMatchesSimType(pkg, formData.sim_type))
            .map(pkg => ({
              value: pkg.id,
              label: `[${pkg.carrier}] ${pkg.name} ${pkg.speed_mbps ? `- ${pkg.speed_mbps} Mbps` : ''} ${pkg.data_quota_gb ? `(${pkg.data_quota_gb} GB)` : '(Unlimited)'} - ${packagePriceLabel(pkg)}`
            }))
            // KD3 S2115 (BUG-S2115-001): แพ็กเกจที่ถูกปิดใช้งานไม่อยู่ในรายการ "ใช้งาน" → ช่องขึ้นว่าง
            // ทั้งที่ซิมยังผูกอยู่ · เติมค่าปัจจุบันกลับพร้อมป้าย (ปิดใช้งาน)
            .concat(
              mode !== 'create' && formData.sim_package_id
                && !simPackages.some(p => p.id === formData.sim_package_id)
                && formData.sim_package_id === (selectedSim as any)?.sim_package_id
                ? [{ value: formData.sim_package_id, label: `${(selectedSim as any)?.package_name || `แพ็กเกจ #${formData.sim_package_id}`} (ปิดใช้งาน)` }]
                : []
            )}
          value={formData.sim_package_id || ''}
          onChange={(value) => {
            handlePackageSelect(value ? Number(value) : null);
            if (formErrors.sim_package_id) setFormErrors((prev) => ({ ...prev, sim_package_id: '' }));
          }}
          placeholder="-- เลือกแพ็กเกจซิมจากรายการ --"
          searchPlaceholder="ค้นหาแพ็กเกจ..."
        />
        {formErrors.sim_package_id && (
          <p className="mt-1 text-sm text-danger-600 flex items-center">
            <RiErrorWarningLine className="w-4 h-4 mr-1" />
            {formErrors.sim_package_id}
          </p>
        )}
        <p className="mt-1 text-xs text-primary-600">
          เลือกแพ็กเกจจากข้อมูลหลัก ระบบจะบันทึก ความเร็ว / รอบบิล / ค่าบริการ / ค่ายมือถือ ตามแพ็กเกจให้อัตโนมัติ
        </p>
        {(() => {
          const pkg = simPackages.find((p) => p.id === formData.sim_package_id);
          if (!pkg) return null;
          const billingLabel = pkg.billing_cycle === 'YEARLY' ? 'รายปี' : 'รายเดือน';
          return (
            <div className="mt-3 pt-3 border-t border-primary-200 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-primary-800">
              <div><span className="block text-primary-500">ค่ายมือถือ</span><span className="font-medium">{pkg.carrier}</span></div>
              <div><span className="block text-primary-500">ความเร็ว</span><span className="font-medium">{pkg.speed_mbps ? `${pkg.speed_mbps} Mbps` : '-'}</span></div>
              <div><span className="block text-primary-500">โควต้า</span><span className="font-medium">{pkg.data_quota_gb ? `${pkg.data_quota_gb} GB` : 'Unlimited'}</span></div>
              <div><span className="block text-primary-500">รอบบิล</span><span className="font-medium">{billingLabel}</span></div>
              <div><span className="block text-primary-500">ค่าบริการ</span><span className="font-medium">{packagePriceLabel(pkg)}</span></div>
            </div>
          );
        })()}
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-1">ผู้ลงทะเบียน</label>
        <SearchableSelect
          name="registered_by_user_id"
          options={userOptions}
          value={formData.registered_by_user_id !== null ? formData.registered_by_user_id : ''}
          onChange={(value) => setFormData((prev) => ({ ...prev, registered_by_user_id: value ? Number(value) : null }))}
          placeholder="-- เลือกผู้ลงทะเบียน --"
          searchPlaceholder="ค้นหาผู้ใช้..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ValidatedInput
          label="วันที่เปิดใช้งาน"
          name="activation_date"
          type="date"
          value={formData.activation_date}
          onChange={handleInputChange}
          error={formErrors.activation_date}
        />
        {/* ซิมรายเดือน (POSTPAID) ไม่มีวันหมดอายุ — ซ่อนช่อง (BE force null อยู่แล้ว) */}
        {formData.sim_type !== SimType.POSTPAID && (
          <ValidatedInput
            label="วันหมดอายุ"
            name="expiry_date"
            type="date"
            value={formData.expiry_date}
            onChange={handleInputChange}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="สถานะ"
          name="status"
          value={formData.status}
          onChange={handleInputChange}
          error={formErrors.status}
          required
          // RESERVED ตั้งโดยใบงานเตรียมเครื่องเท่านั้น · ISSUED ตั้งโดยใบเบิก-ยืมเท่านั้น — ล็อกช่อง
          disabled={
            formData.status === SimStatus.RESERVED ||
            formData.status === SimStatus.ISSUED
          }
          // ล็อกช่องอย่างเดียวไม่พอ — ต้องบอกทางออกด้วย (asymmetry เครื่อง/ซิม จุดที่ 5)
          helperText={
            formData.status === SimStatus.RESERVED
              ? 'ซิมนี้ถูกกันไว้ให้ใบงานเตรียมเครื่อง — ปล่อยของได้ที่เมนู "การเช่า › ใบงานเตรียมเครื่อง" โดยถอดซิมออกจากใบงานหรือยกเลิกใบงาน'
              : formData.status === SimStatus.ISSUED
                ? `ซิมถูกเบิกตามใบเบิก${selectedSim?.current_stock_issue_number ? ` ${selectedSim.current_stock_issue_number}` : ''} — คืนที่หน้าใบเบิก-ยืม`
                : undefined
          }
        >
          {/* RESERVED / ISSUED ไม่ใช่ตัวเลือกให้เลือกเอง แต่ต้องมี <option> รองรับค่าปัจจุบัน
              ไม่งั้น select fallback ไป option แรก แล้วกดบันทึกทับสถานะจริง */}
          {formData.status === SimStatus.RESERVED && (
            <option value={SimStatus.RESERVED}>จองไว้ (ใบงานเตรียมเครื่อง)</option>
          )}
          {formData.status === SimStatus.ISSUED && (
            <option value={SimStatus.ISSUED}>เบิกไปใช้ภายใน</option>
          )}
          <option value={SimStatus.AVAILABLE}>พร้อมใช้งาน</option>
          <option value={SimStatus.IN_USE}>กำลังใช้งาน</option>
          <option value={SimStatus.INACTIVE}>ไม่ใช้งาน</option>
          <option value={SimStatus.SUSPENDED}>ระงับชั่วคราว</option>
          <option value={SimStatus.EXPIRED}>หมดอายุ</option>
          <option value={SimStatus.ACTIVE}>ใช้งาน (เดิม)</option>
        </Select>
      </div>

      <ValidatedTextarea label="หมายเหตุ" name="notes" value={formData.notes} onChange={handleInputChange} rows={3} />
    </div>
  );
};

export default SimFormFields;
